import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, type ApolloError } from "@apollo/client";
import DetailsCard from "@/components/DetailsCard";
import SubSectionHeading from "@/components/SubSectionHeading";
import MultiSelect from "@/components/MultiSelect";
import Select from "@/components/Select";
import Chip, { ChipSet } from "@/components/Chip";
import EmptyState from "@/components/EmptyState";
import MetricGrid from "@/components/MetricGrid";
import DateRangeScroller, {
  parsePeriodFromSearch,
  periodToSearch,
  type PeriodWindow
} from "@/components/DateRangeScroller";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { sendToRaygun } from "@/lib/raygunUtils";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import { CALL_TOPIC_STATS_QUERY } from "./queries";
import TopicCharts from "./_components/TopicCharts";
import TopicHighlights from "./_components/TopicHighlights";
import RecentCallsCard from "./_components/RecentCallsCard";
import TeamLeaderboard from "./_components/TeamLeaderboard";
import {
  buildWorkerOptions,
  durationDeltaText,
  filterRecentCallsBySelection,
  filterTopicsBySelection,
  formatCount,
  formatDuration,
  formatPercent,
  percentOf,
  teamAverageDuration,
  teamTotalCalls,
  workerDisplayName
} from "./_utils";

const PAGE_PATH = "/analytics/interactions/membership-services";

type DirectionFilter = "all" | "inbound" | "outbound";

const DIRECTION_OPTIONS: { value: DirectionFilter; label: string }[] = [
  { value: "all", label: "All directions" },
  { value: "inbound", label: "Inbound" },
  { value: "outbound", label: "Outbound" }
];

type AnsweredFilter = "all" | "answered" | "unanswered";

const ANSWERED_OPTIONS: { value: AnsweredFilter; label: string }[] = [
  { value: "all", label: "All calls" },
  { value: "answered", label: "Answered" },
  { value: "unanswered", label: "Unanswered" }
];

export default function MembershipServicesTabPage() {
  usePageTitle(PAGE_PATH, "Interaction Analytics | Membership Services");
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeopleInteractions });

  const [searchParams, setSearchParams] = useSearchParams();
  const period = useMemo(() => parsePeriodFromSearch(searchParams), [searchParams]);
  // Null topics = all selected. Resolved once stats load so the toggle-all affordance works.
  const [topics, setTopics] = useState<string[] | null>(null);
  const [direction, setDirection] = useState<DirectionFilter>("all");
  const [answered, setAnswered] = useState<AnsweredFilter>("all");
  const [workerName, setWorkerName] = useState<string | null>(null);
  const [drilldownTopic, setDrilldownTopic] = useState<string | null>(null);
  const { setError, clearError } = useErrorBanner();

  const startDateTime = period.start.toUTC().toISO() ?? "";
  const endDateTime = period.end.toUTC().toISO() ?? "";

  const { data, previousData, loading, error, refetch } = useQuery(CALL_TOPIC_STATS_QUERY, {
    variables: {
      startDateTime,
      endDateTime,
      direction: direction === "all" ? null : direction,
      answered: answered === "all" ? null : answered === "answered",
      workerName
    },
    skip: !startDateTime || !endDateTime,
    notifyOnNetworkStatusChange: true,
    onCompleted: () => clearError(),
    onError: (queryError: ApolloError) => {
      sendToRaygun(queryError, { tags: ["call-topics", "stats"] });
      setError(queryError.message);
    }
  });

  const stats = (data ?? previousData)?.callTopicStats;
  const workers = useMemo(() => stats?.workers ?? [], [stats]);
  const workerOptions = useMemo(() => buildWorkerOptions(workers), [workers]);
  const teamAvgDurationSeconds = useMemo(() => teamAverageDuration(workers), [workers]);
  const teamCalls = useMemo(() => teamTotalCalls(workers), [workers]);
  const selectedWorker = workerName ? (workers.find((entry) => entry.workerName === workerName) ?? null) : null;
  const allTopics = useMemo(() => (stats?.topics ?? []).map((bucket) => bucket.topic), [stats]);
  const selectedTopics = topics ?? allTopics;
  const filtered = useMemo(() => filterTopicsBySelection(stats?.topics ?? [], selectedTopics), [stats, selectedTopics]);
  const recentCalls = useMemo(
    () => filterRecentCallsBySelection(stats?.recentCalls ?? [], selectedTopics),
    [stats, selectedTopics]
  );
  const topicCounts = useMemo(
    () => new Map((stats?.topics ?? []).map((bucket) => [bucket.topic, bucket.count])),
    [stats]
  );
  const topicOptions = useMemo(
    () => allTopics.map((topic) => ({ value: topic, label: `${topic} (${topicCounts.get(topic) ?? 0})` })),
    [allTopics, topicCounts]
  );
  const drilldownChoices = selectedTopics.length > 0 ? selectedTopics : allTopics;
  const effectiveDrilldown =
    (drilldownTopic && drilldownChoices.includes(drilldownTopic) && drilldownTopic) ||
    filtered.topTopic?.topic ||
    drilldownChoices[0] ||
    "Other";
  const latestCallAt = stats?.recentCalls[0]?.startedAt;
  const busy = loading && !stats;

  const updatePeriod = (next: PeriodWindow) => {
    setSearchParams(periodToSearch(next), { replace: true });
  };

  if (error && !stats) {
    return (
      <EmptyState
        title="Unable to load call topics"
        caption="Try refreshing the page. If the problem continues, contact support."
        iconName="error"
        action={{ children: "Try Again", onClick: () => refetch() }}
      />
    );
  }

  return (
    <div data-testid="call-topics-page">
      <DetailsCard compact className="mt-12">
        <div className="grid w-full gap-4 lg:grid-cols-2">
          <div className="flex w-full flex-col items-start gap-2 lg:col-span-2">
            <SubSectionHeading>Filter By Date Range</SubSectionHeading>
            <DateRangeScroller value={period} onChange={updatePeriod} />
          </div>
          <div className="flex w-full flex-col items-start gap-2">
            <SubSectionHeading>Filter By Topic</SubSectionHeading>
            <div className="flex w-full flex-wrap items-center gap-4">
              <div className="shrink-0">
                <MultiSelect
                  label="Topics"
                  options={topicOptions}
                  selectedValues={selectedTopics}
                  selectedText={
                    topics === null
                      ? "All topics"
                      : selectedTopics.length === 0
                        ? "None selected"
                        : selectedTopics.length === allTopics.length
                          ? "All topics"
                          : `${selectedTopics.length} selected`
                  }
                  selectAllLabel="Select All"
                  selectNoneLabel="Select None"
                  closeOnSelectAll
                  searchable
                  onSelect={(value, checked) => {
                    const baseline = topics ?? allTopics;
                    setTopics(
                      checked
                        ? [...baseline, value].filter((topic, index, list) => list.indexOf(topic) === index)
                        : baseline.filter((topic) => topic !== value)
                    );
                  }}
                  onClear={() => setTopics(null)}
                  onSelectNone={() => setTopics([])}
                  className="w-[18rem] max-w-full"
                />
              </div>
              <div role="group" aria-label="Direction" className="min-w-0">
                <ChipSet>
                  {DIRECTION_OPTIONS.map((option) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      selected={direction === option.value}
                      onClick={() => setDirection(option.value)}
                    />
                  ))}
                </ChipSet>
              </div>
              <div role="group" aria-label="Answer status" className="min-w-0">
                <ChipSet>
                  {ANSWERED_OPTIONS.map((option) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      selected={answered === option.value}
                      onClick={() => setAnswered(option.value)}
                    />
                  ))}
                </ChipSet>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-2">
            <SubSectionHeading>Filter By Team Member</SubSectionHeading>
            <div className="w-[18rem] max-w-full">
              <Select
                label="Team member"
                options={workerOptions}
                value={workerName ?? ""}
                placeholder="All members"
                searchable
                onSelect={setWorkerName}
                onClear={() => setWorkerName(null)}
                data-testid="call-topics-worker-select"
              />
            </div>
          </div>
        </div>
      </DetailsCard>

      <MetricGrid
        layout="expanded"
        metrics={[
          {
            label: "Total calls",
            value: busy ? undefined : formatCount(filtered.total),
            loading: busy,
            iconName: "call",
            testId: "call-topics-total-metric",
            tooltip: {
              supportingText: selectedWorker
                ? `${workerDisplayName(workerName)} handled ${formatCount(filtered.total)} of ${formatCount(teamCalls)} team calls (${formatPercent(percentOf(filtered.total, teamCalls))}).`
                : "Flex calls started in range matching the selected filters."
            }
          },
          {
            label: "Classified",
            value: busy ? undefined : formatPercent(percentOf(filtered.classified, filtered.total)),
            loading: busy,
            iconName: "check_circle",
            testId: "call-topics-classified-metric",
            tooltip: {
              supportingText: "Share of selected calls with an AI topic. The rest are still being analyzed."
            }
          },
          {
            label: "Avg duration",
            value: busy ? undefined : formatDuration(filtered.avgDurationSeconds),
            loading: busy,
            iconName: "timelapse",
            testId: "call-topics-duration-metric",
            tooltip: {
              supportingText: selectedWorker
                ? `Team average is ${formatDuration(teamAvgDurationSeconds)} (${durationDeltaText(filtered.avgDurationSeconds, teamAvgDurationSeconds)}).`
                : "Mean talk time across selected calls that have ended."
            }
          },
          {
            label: "Top topic",
            value: busy ? undefined : (filtered.topTopic?.topic ?? "—"),
            loading: busy,
            iconName: "bar_chart",
            testId: "call-topics-top-topic-metric",
            tooltip: {
              supportingText: filtered.topTopic
                ? `${formatCount(filtered.topTopic.count)} calls in range.`
                : "No calls match the current filters."
            }
          }
        ]}
      />
      {latestCallAt && (
        <p className="text-body-medium text-sys-brand-on-surface-variant mt-4" data-testid="call-topics-freshness">
          Latest call {toDateTimeFormat(latestCallAt)}
        </p>
      )}

      <div className="mt-8">
        <TopicHighlights
          startDateTime={startDateTime}
          endDateTime={endDateTime}
          direction={direction === "all" ? null : direction}
          answered={answered === "all" ? null : answered === "answered"}
          topics={selectedTopics}
          workerName={workerName}
          enabled
        />
      </div>

      <TopicCharts
        topics={filtered.topics}
        days={stats?.days ?? []}
        totalCalls={filtered.total}
        analyzedCalls={filtered.classified}
        drilldownTopic={effectiveDrilldown}
        drilldownOptions={drilldownChoices.map((topic) => ({ value: topic }))}
        onDrilldownTopicChange={setDrilldownTopic}
        loading={busy}
      />

      <div className="pt-12">
        <TeamLeaderboard
          workers={workers}
          teamAvgDurationSeconds={teamAvgDurationSeconds}
          selectedWorkerName={workerName}
          onSelectWorker={setWorkerName}
          loading={busy}
        />
      </div>

      <div className="pt-12">
        <RecentCallsCard calls={recentCalls} loading={busy} />
      </div>
    </div>
  );
}
